/* External */
import {
  useState, memo, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import Icon from '@dtvgo/icon';

/* Utils */
import FocusableInput from '../../../../../components/FocusableInput';
import FocusableButtonProfile from '../../../../../components/FocusableButtonProfile';
import { useOnBackPress } from '../../../../../utils/keyDownEvents';
import { Routes, getLink } from '../../../../../utils/router';
import { TYPE_ACTIONS_PROFILE } from '../../../../../utils/constants';
import { useCountry } from '../../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../../providers/GtmLocalProvider';

/* GTM */
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../../utils/googleTags';

/* Storage */
import { editProfile } from '../../../../../storage/profiles/actions';
import DefaultKeyboard from '../../../../../components/DefaultKeyboard';

import { editProfileAPI, createProfileAPI } from '../../../../../services/profiles';

/* Style */
import './DefaultCreateProfileName.scss';

function DefaultCreateProfileName({ realFocusKey, setFocus }) {
  const [name, setName] = useState('');
  const history = useHistory();
  const params = useParams();
  const dispatch = useDispatch();
  const innerRef = useRef();
  const location = useLocation();
  const userProfiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  const [profiles] = useState(userProfiles);
  const selectedProfile = profiles?.find((profile) => profile?.id === params.id);
  const createProfileFocusKey = `${realFocusKey}-first-focus-default-create-profile`;
  const newProfileName = `Perfil ${(profiles?.length || 0) + 1}`;
  const [showKeyboard, setShowKeyboard] = useState(false);
  const indexColor = location.state?.indexColor;
  const typeCreateName = location.state?.typeCreateName;
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const isCreatingProfile = useRef(false);
  const [warn, setWarn] = useState(false);
  const maxLenght = 16;

  const profileCommonTagEvent = useCallback(({ subCategory, eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: subCategory || GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE_NAME,
        eventName,
      });
    }
  }, [commonTaggingEvent]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      if (typeCreateName === TYPE_ACTIONS_PROFILE.EDIT) {
        profileCommonTagEvent({
          eventName: GTM_EVENT_NAMES.PROFILES_CREATE_NAME.PAGE_VIEW_EDIT,
          subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE_NAME,
        });
      } else if (typeCreateName === TYPE_ACTIONS_PROFILE.ONBOARDING) {
        profileCommonTagEvent({
          eventName: GTM_EVENT_NAMES.PROFILES_ONBOARDING.PAGE_VIEW_CREATE_NAME,
          subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_ONBOARDING,
        });
      } else {
        profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      }
      tagsMountRef.current = false;
    }
  }, [profileCommonTagEvent, country, typeCreateName]);

  useEffect(() => {
    if (params.id) {
      setName(selectedProfile.name);
    } else {
      setName(newProfileName);
    }
  }, [newProfileName, params.id, selectedProfile?.name]);

  useEffect(() => {
    if (name.length === 0) {
      setWarn(true);
    } else {
      setWarn(false);
    }

    innerRef.current.value = name;
  }, [name]);

  useEffect(() => {
    setFocus(createProfileFocusKey);
  }, [createProfileFocusKey, setFocus]);

  const onInput = useCallback((text) => {
    const profileName = text.target.value;
    // Removes empty spaces on the sides of the string.
    setName(profileName.replace(/^\s+|\s+$/g, ''));
  }, []);

  const validationButton = useCallback(() => (
    !name
    || name.replace(/\s/g, '').length === 0
  ), [name]);

  const handleClick = useCallback(
    () => {
      if (validationButton() === false && !isCreatingProfile.current) {
        isCreatingProfile.current = true;
        if (typeCreateName === TYPE_ACTIONS_PROFILE.EDIT) {
          profileCommonTagEvent({
            eventName: GTM_EVENT_NAMES.PROFILES_CREATE_NAME.CONTINUE_PROFILE_EDIT,
            subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE_NAME,
          });
        } else if (typeCreateName === TYPE_ACTIONS_PROFILE.ONBOARDING) {
          profileCommonTagEvent({
            eventName: GTM_EVENT_NAMES.PROFILES_ONBOARDING.CONTINUE_CREATE_NAME_ONBOARDING,
            subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_ONBOARDING,
          });
        } else {
          profileCommonTagEvent({
            eventName: GTM_EVENT_NAMES.PROFILES_CREATE_NAME.CONTINUE_PROFILE,
          });
        }

        if (name) {
          if (params.id && typeCreateName !== TYPE_ACTIONS_PROFILE.ONBOARDING) {
            editProfileAPI(params.id, { name }).then((res) => {
              dispatch(editProfile(res.data.profiles));
              localStorage.setItem('profiles', JSON.stringify(res.data.profiles));
              history.push(
                getLink(Routes.Private.Profiles.Edit, { id: params.id }),
                { indexColor },
              );
              return true;
            }).catch((error) => {
              console.error(error);
              history.push(getLink(Routes.Private.Profiles.Error), {
                source: 'MIDDLEWARE',
                location: 'PROFILE_EDIT',
                type: 'UNEXPECTED',
                externCode: error?.response?.status,
                typeError: TYPE_ACTIONS_PROFILE.EDIT,
              });
              return false;
            });
          } else if (typeCreateName !== TYPE_ACTIONS_PROFILE.ONBOARDING) {
            createProfileAPI({
              name,
              avatar: '',
              ageRating: '18',
            }).then((res) => {
              dispatch(editProfile(res.data.profiles));
              localStorage.setItem('profiles', JSON.stringify(res.data.profiles));
              history.push(getLink(Routes.Private.Profiles), { create: true });
              return true;
            }).catch((error) => {
              console.error(error);
              history.push(getLink(Routes.Private.Profiles.Error), {
                source: 'MIDDLEWARE',
                location: 'PROFILE_EDIT',
                type: 'UNEXPECTED',
                externCode: error?.response?.status,
                typeError: TYPE_ACTIONS_PROFILE.EDIT,
              });
              return false;
            });
          } else if (profiles?.length > 0) {
            history.push(getLink(Routes.Private.Profiles.Create), { username: name });
          } else {
            history.push(getLink(Routes.Private.Profiles.Create.Confirmation), { username: name });
          }
        }
      }
    },
    [
      dispatch,
      history,
      indexColor,
      name,
      params.id,
      profiles?.length,
      profileCommonTagEvent,
      typeCreateName,
      validationButton,
    ],
  );

  const focusInput = useCallback(() => {
    if (document.activeElement === innerRef.current) {
      innerRef.current.blur();
      setShowKeyboard(false);
    } else {
      setShowKeyboard(true);
      setFocus('KeyboardFocusable');
    }
  }, [setFocus]);

  function inputValue() {
    if (name) {
      return name;
    }
    return params.id ? selectedProfile.name : newProfileName;
  }

  const closeKeyboard = useCallback(() => {
    innerRef.current.blur();
    setShowKeyboard(false);
    setFocus(createProfileFocusKey);
  }, [createProfileFocusKey, setFocus]);

  const handleBackPress = useCallback(() => {
    if (typeCreateName === TYPE_ACTIONS_PROFILE.EDIT) {
      profileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PROFILES_EDIT.BACK_PROFILES,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_EDIT,
      });
    } else {
      profileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PROFILES_CREATE.BACK_PROFILES,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
      });
    }
    if (showKeyboard === true) {
      innerRef.current.blur();
      setShowKeyboard(false);
      setFocus(createProfileFocusKey);
    } else {
      history.push(getLink(Routes.Private.Profiles));
    }
  }, [
    createProfileFocusKey,
    history,
    setFocus,
    showKeyboard,
    profileCommonTagEvent,
    typeCreateName,
  ]);

  useOnBackPress({ listener: handleBackPress });

  return (
    <div className="dtv-html5-c-default-create-profile">
      <div className="dtv-html5-c-default-create-profile--div">
        <h1>
          {profiles?.length > 0 ? (<Icon className="dtv-html5-c-default-create-profile__icon-back-logo" name="arrow-back" />) : ''}
          {selectedProfile?.id ? <FormattedMessage id="profile.change" /> : <FormattedMessage id="profile.create" />}
        </h1>
        <form className="dtv-html5-c-default-create-profile--div-form">
          <FocusableInput
            id="namePerfil"
            type="text"
            name="namePerfil"
            required
            labelContent={<FormattedMessage id="profile.call.name" />}
            createUser
            isInputActive
            onInput={onInput}
            inputValue={() => { inputValue(); }}
            onEnterPress={focusInput}
            innerRef={innerRef}
            focusKey={createProfileFocusKey}
            disabled
            emptyInputText={<FormattedMessage id="profiles.userProfile.editProfile.emptyInputText" />}
            warn={warn}
            warnMsg={<FormattedMessage id="profiles.userProfile.editProfile.emptyInputText" />}
          />
          {profiles?.length === 0 ? (
            <p>
              <FormattedMessage id="profile.main" />
            </p>
          ) : ''}
          {showKeyboard ? (
            <DefaultKeyboard
              className={showKeyboard ? 'show' : 'hide'}
              confirm={closeKeyboard}
              deleteCharacter={() => setName((prevName) => prevName.slice(0, -1))}
              addSpace={() => { setName((prevName) => `${prevName} `); }}
              addCharacter={(e) => {
                setName((prevName) => {
                  const newName = prevName + e;
                  return newName.length <= maxLenght ? newName : prevName;
                });
              }}
              blockNavigationOut
              focusKey="KeyboardFocusable"
            />
          ) : ''}
          <div className="dtv-html5-c-default-create-profile--div-buttonAlignment">
            <FocusableButtonProfile
              className={validationButton() ? 'dtv-btn-tv dtv-html5-c-default-create-profile__button' : 'dtv-btn-blue dtv-html5-c-default-create-profile__button'}
              secondGray
              bordered
              disabled={validationButton()}
              onEnterPress={handleClick}
            >
              <FormattedMessage id="common.continue" />
            </FocusableButtonProfile>
          </div>
        </form>
      </div>
    </div>
  );
}

DefaultCreateProfileName.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(DefaultCreateProfileName)));
